/**
 * Client-side configuration for the Recurring Value Podcast Admin Portal
 * 
 * Uses Vite's import.meta.env for environment variables instead of process.env
 * to ensure browser compatibility.
 */
import { z } from 'zod';

// Define environment schema with validation
const envSchema = z.object({
  // Node environment
  NODE_ENV: z.enum(['development', 'test', 'production']).default('development'),
  
  // Clerk authentication
  CLERK_PUBLISHABLE_KEY: z.string().min(1, "Clerk publishable key is required").default(''),
  
  // Supabase
  SUPABASE_URL: z.string().url("Supabase URL must be a valid URL").default('https://example.supabase.co'),
  SUPABASE_ANON_KEY: z.string().min(1, "Supabase anon key is required").default(''),
  
  // Mapbox
  MAPBOX_TOKEN: z.string().min(1, "Mapbox token is required").default(''),
  
  // Application URLs
  APP_URL: z.string().url().default('http://localhost:5173'),
  API_URL: z.string().url().optional(),
});

// Process environment variables using Vite's import.meta.env
const processEnv = {
  NODE_ENV: import.meta.env.MODE || 'development',
  CLERK_PUBLISHABLE_KEY: import.meta.env.VITE_CLERK_PUBLISHABLE_KEY || '',
  SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL || 'https://example.supabase.co',
  SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY || '',
  MAPBOX_TOKEN: import.meta.env.VITE_MAPBOX_TOKEN || '',
  APP_URL: import.meta.env.VITE_APP_URL || 'http://localhost:5173',
  API_URL: import.meta.env.VITE_API_URL,
};

// Parse and validate environment variables
const env = envSchema.safeParse(processEnv);

// Handle validation errors
if (!env.success) {
  console.error('❌ Invalid environment variables:');
  console.error(env.error.format());
  // Don't throw in production to prevent app crashes
  if (import.meta.env.DEV) {
    console.warn('Continuing with default values in development mode');
  }
}

// Use validated environment variables or fallback to defaults
const validatedEnv = env.success ? env.data : processEnv;

// Export validated environment variables
export const config = {
  environment: validatedEnv.NODE_ENV,
  isDevelopment: validatedEnv.NODE_ENV === 'development',
  isProduction: validatedEnv.NODE_ENV === 'production',
  isTest: validatedEnv.NODE_ENV === 'test',
  clerk: {
    publishableKey: validatedEnv.CLERK_PUBLISHABLE_KEY,
  },
  supabase: {
    url: validatedEnv.SUPABASE_URL,
    anonKey: validatedEnv.SUPABASE_ANON_KEY,
  },
  mapbox: {
    token: validatedEnv.MAPBOX_TOKEN,
  },
  urls: {
    app: validatedEnv.APP_URL,
    api: validatedEnv.API_URL,
  },
};

// Export default configuration
export default config;
